import React, { useMemo } from 'react';
import { Spin } from 'antd';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import {
	CHART_PRIMARY_COLOR,
	COMPARISON_PERIOD_COLOR,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { toMonthChartRow } from '../../utils/DataFormatterUtils';
import { buildSeriesComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	getAnalyticsRolePrimaryColor,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	ColumnLineComboChart,
	pairComparisonByMonth,
} from '../analytics_charts';
import { useAnalyticsBreakdownComparison } from '../../hooks/useAnalyticsBreakdownComparison';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';

export type KPIWorkOrdersChartProps = {
	data: any[];
	loading: boolean;
	filters: Record<string, any>;
	fetchComparisonData: (params: Record<string, any>) => Promise<any[]>;
	userType: string;
	currency: {
		id: string;
		format: (val: number, decimals?: number) => string;
		symbolFormat: (val: number, decimals?: number) => string;
	};
	/** Lifetime / aggregate summary used in the header. */
	totalWOs: number | null | undefined;
	averageWOCost: number | null | undefined;
};

const toMonthRow = (row: any) => ({
	...toMonthChartRow(row),
	totalWOs: parseFloat(nullSafeGetOrElse('totalWOs', row, 0)) || 0,
	averageWOCost: parseFloat(nullSafeGetOrElse('averageWOCost', row, 0)) || 0,
});

const KPIWorkOrdersChart = ({
	data,
	loading,
	filters,
	fetchComparisonData,
	userType,
	currency,
	totalWOs,
	averageWOCost,
}: KPIWorkOrdersChartProps) => {
	const currentColor = getAnalyticsRolePrimaryColor(userType) || CHART_PRIMARY_COLOR;
	const previousColor = COMPARISON_PERIOD_COLOR;

	const {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison,
	} = useAnalyticsBreakdownComparison({
		filters,
		fetchComparisonData,
	});

	const currentRows = useMemo(() => (data || []).map(toMonthRow), [data]);
	const previousRows = useMemo(() => (comparisonRows || []).map(toMonthRow), [comparisonRows]);

	const chartData = useMemo(() => {
		const paired = pairComparisonByMonth({
			current: currentRows,
			previous: previousRows,
			hasComparison,
			selectedRange: selectedStart && selectedEnd ? [selectedStart, selectedEnd] : null,
			comparisonRange,
			buildPreviousRow: (prevRow: any, curRow: any) => ({
				...(prevRow || {}),
				// Preserve the previous period's real month label as a separate field so
				// ColumnLineComboChart can stamp it under the previous bar in the dodged pair —
				// while `monthLabel` is overwritten with the current month so both bars share
				// the same x-axis band. Mirrors lifecycle SpendChart's buildPreviousRow.
				previousMonthLabel: prevRow ? prevRow.monthLabel : '',
				previousMonthFull: prevRow ? prevRow.monthFull : '',
				monthLabel: curRow.monthLabel,
				monthFull: curRow.monthFull,
			}),
		});
		return [...paired.previous, ...paired.current];
	}, [currentRows, previousRows, hasComparison, selectedStart, selectedEnd, comparisonRange]);

	const formatWOCount = (val: any) => {
		const num = parseFloat(val);
		if (isNaN(num)) return '0';
		return String(Math.round(num));
	};

	const formatCost = (val: any) => {
		const num = parseFloat(val);
		if (isNaN(num)) return currency.symbolFormat(0, 0);
		return currency.symbolFormat(num, 0);
	};

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		// G2 fires one tooltip item per geom that hits the crosshair (column + line), so each
		// current/previous period shows up twice in `items`. De-dup by series before mapping so
		// the tooltip lists the # WOs row exactly once per period.
		const seenSeries = new Set<string>();
		const rows = items
			.slice()
			.sort((a, b) => Number(isCurrentItem(b)) - Number(isCurrentItem(a)))
			.filter((item) => {
				const seriesKey = item.data && item.data[COMPARISON_SERIES_FIELD];
				if (!seriesKey || seenSeries.has(seriesKey)) return false;
				seenSeries.add(seriesKey);
				return true;
			})
			// The combo chart plots both a # WOs column and an avg-cost line per period. Emit a row
			// for each so the tooltip surfaces the cost values too — not just the work order count.
			.flatMap((item) => {
				const monthFull = (item.data && item.data.monthFull) || title;
				const color = isCurrentItem(item) ? currentColor : previousColor;
				const dashed = !isCurrentItem(item);
				return [
					{
						label: `${monthFull} — # WOs`,
						color,
						value: formatWOCount(item.data && item.data.totalWOs),
						dashed,
					},
					{
						label: `${monthFull} — Avg cost`,
						color,
						value: formatCost(item.data && item.data.averageWOCost),
						dashed,
					},
				];
			});
		return buildSeriesComparisonTooltipHtml(rows);
	};
	const renderSingleTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const row = (items[0] && items[0].data) || {};
		const monthFull = row.monthFull || title;
		const tooltipRows = [
			{
				label: '# Work Orders',
				color: currentColor,
				value: formatWOCount(row.totalWOs),
			},
		];
		if (parseFloat(row.averageWOCost) > 0) {
			tooltipRows.push({
				label: 'Avg cost',
				color: currentColor,
				value: formatCost(row.averageWOCost),
			});
		}
		return buildSeriesComparisonTooltipHtml(tooltipRows, { title: monthFull });
	};

	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const summaryText =
		totalWOs && totalWOs > 0
			? `${totalWOs} WOs · avg ${currency.format(averageWOCost || 0, 0)}`
			: 'No work orders';

	const title = <AnalyticsChartHeader label="Work Orders" value={summaryText} />;

	const handleDownload = () => {
		if (!currentRows || currentRows.length === 0) return;
		const includeComparison = hasComparison && previousRows.length > 0;
		const baseHeaders = ['Month', '# Work Orders', 'Avg Work Order Cost'];
		const headers = includeComparison ? [...baseHeaders, 'Historical Data'] : baseHeaders;

		const mapRow = (row: any, isHistorical: boolean): Record<string, string | number> => {
			const base: Record<string, string | number> = {
				month: row.monthFull || row.monthLabel || '',
				wos: formatWOCount(row.totalWOs),
				cost: formatCost(row.averageWOCost),
			};
			if (includeComparison) {
				// Match the lifecycle/breakdown CSV pattern: "Yes" for previous-period rows so the
				// reader can filter or pivot historical vs current.
				base.historicalData = isHistorical ? 'Yes' : 'No';
			}
			return base;
		};

		const previousExportRows = includeComparison
			? previousRows.map((row: any) => mapRow(row, true))
			: [];
		const currentExportRows = currentRows.map((row: any) => mapRow(row, false));

		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: 'Work Orders',
			title: 'Work Orders',
			headers,
		});
		csvExporter.generateCsv([...previousExportRows, ...currentExportRows]);
	};

	const extra = (
		<AnalyticsChartActions
			comparison={{
				value: comparisonState || { mode: COMPARISON_MODE.NONE },
				onChange: setComparison,
				selectedStart,
				selectedEnd,
				isTimeBased: true,
				loading: !!comparisonLoading,
				disabled: !!comparisonDisabled,
				disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
			}}
			onDownload={handleDownload}
		/>
	);

	return (
		<AnalyticsChartCard title={title} extra={extra} className="h-full">
			<Spin spinning={loading || comparisonLoading}>
				<div className="w-full min-w-0">
					<ColumnLineComboChart
						data={chartData}
						columnYField="totalWOs"
						lineYField="averageWOCost"
						hasComparison={hasComparison}
						currentColor={currentColor}
						previousColor={previousColor}
						cols={{
							totalWOs: { alias: '# Work Orders' },
							averageWOCost: { alias: 'Average Work Order Cost' },
						}}
						columnAxisYTitle="# Work Orders"
						lineAxisYTitle={`Average Cost (${currency.id})`}
						formatColumnYAxisLabel={formatWOCount}
						formatLineYAxisLabel={formatCost}
						renderComparisonTooltip={renderComparisonTooltip}
						renderSingleTooltip={renderSingleTooltip}
						height={300}
					/>
					{hasComparison ? (
						<ComparisonLegend
							currentStart={selectedStart}
							currentEnd={selectedEnd}
							previousStart={comparisonStart}
							previousEnd={comparisonEnd}
							currentColor={currentColor}
							previousColor={previousColor}
							mode={comparisonState.mode}
						/>
					) : (
						<div className="mt-3 h-5" aria-hidden="true" />
					)}
				</div>
			</Spin>
		</AnalyticsChartCard>
	);
};

export default KPIWorkOrdersChart;
